import { useCallback, useEffect, useRef, useState } from "react";

export type DockTab = "contexto" | "minimapa";

/** A pushed detail view: a cited source or a background investigation. */
export type DockDetail =
  | { kind: "source"; id: string }
  | { kind: "research"; taskId: string }
  | { kind: "report"; sessionId: string; reportId: string; revisionNumber: number };

const STORAGE_KEY = "beflow.chat.right-dock";

interface StoredDockState {
  open: boolean;
  tab: DockTab;
}

function readStored(): Partial<StoredDockState> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Partial<StoredDockState>;
  } catch {
    return {};
  }
}

function normalizeTab(tab: unknown): DockTab {
  return tab === "minimapa" ? "minimapa" : "contexto";
}

/** Whether the dock occupies inline width in the chat layout — a fixed-width
 * `<aside>` to the right of the messages — rather than rendering as an overlay
 * sheet. True only at `>= xl` when a tab is open or a non-expanded detail is
 * shown. Both the dock `<aside>` and the composer read this so the composer
 * reserves the same right-side width and stays horizontally aligned with the
 * messages above it (otherwise the composer centers over the full width while
 * the messages center over the narrower remaining space). */
export function isInlineDockVisible(state: {
  isXl: boolean;
  open: boolean;
  detail: DockDetail | null;
  detailExpanded: boolean;
}): boolean {
  return state.isXl && (state.open || (state.detail !== null && !state.detailExpanded));
}

export interface RightDockState {
  open: boolean;
  tab: DockTab;
  /** Pushed detail view, or null when the dock shows its tabs. Independent of
   * `open`: opening a citation shows the detail without forcing the tabs open,
   * and `closeDetail` reveals the tab only if `open` was already true. */
  detail: DockDetail | null;
  /** When true, the detail is shown as a wide overlay instead of inside the dock. */
  detailExpanded: boolean;
  openTab: (tab: DockTab) => void;
  close: () => void;
  setTab: (tab: DockTab) => void;
  openDetail: (detail: DockDetail) => void;
  closeDetail: () => void;
  clearReportDetails: () => void;
  expandDetail: () => void;
  collapseDetail: () => void;
}

/**
 * Right-dock state with localStorage persistence, made viewport-aware via
 * `isXl`. Tabs (`open` + `tab`) and the pushed `detail` are independent layers.
 *
 * - The active tab is always restored/persisted; `open` is restored/persisted
 *   only at `>= xl`. `detail` and `detailExpanded` are ephemeral (never stored).
 * - Leaving xl closes the tabs and collapses any expanded detail (below xl the
 *   detail renders as a lightbox sheet, never as a wide overlay), but keeps the
 *   `detail` itself so the sheet can show it.
 * - One-level drill memory: opening a source citation from inside a research
 *   detail stashes the research so `closeDetail` ("back") returns to it.
 */
export function useRightDock(isXl: boolean): RightDockState {
  const [tab, setTabState] = useState<DockTab>(() => normalizeTab(readStored().tab));
  const [open, setOpen] = useState<boolean>(() => (isXl ? readStored().open === true : false));
  const [detail, setDetail] = useState<DockDetail | null>(null);
  const [prevDetail, setPrevDetail] = useState<DockDetail | null>(null);
  const [detailExpanded, setDetailExpanded] = useState(false);

  // Latest-value refs so openDetail/closeDetail stay referentially stable (they
  // are passed to memoized turns) while still reading current detail state.
  const detailRef = useRef(detail);
  detailRef.current = detail;
  const prevDetailRef = useRef(prevDetail);
  prevDetailRef.current = prevDetail;

  useEffect(() => {
    const next: StoredDockState = { tab, open: isXl ? open : readStored().open === true };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Persistence is best-effort; ignore quota/availability errors.
    }
  }, [open, tab, isXl]);

  useEffect(() => {
    if (!isXl) {
      setOpen(false);
      setDetailExpanded(false);
    }
  }, [isXl]);

  const openTab = useCallback((next: DockTab) => {
    setTabState(next);
    setOpen(true);
    setDetail(null);
    setPrevDetail(null);
    setDetailExpanded(false);
  }, []);
  const close = useCallback(() => {
    setOpen(false);
    setDetail(null);
    setPrevDetail(null);
    setDetailExpanded(false);
  }, []);
  const setTab = useCallback((next: DockTab) => setTabState(next), []);
  const openDetail = useCallback((next: DockDetail) => {
    const current = detailRef.current;
    setPrevDetail(
      current !== null && current.kind !== "source" && next.kind === "source" ? current : null,
    );
    setDetail(next);
    setDetailExpanded(false);
  }, []);
  const closeDetail = useCallback(() => {
    // Pop the stashed research detail if we drilled into a source from it;
    // otherwise (prev is null) this clears the detail and reveals the tab.
    setDetail(prevDetailRef.current);
    setPrevDetail(null);
    setDetailExpanded(false);
  }, []);
  const clearReportDetails = useCallback(() => {
    if (detailRef.current?.kind === "report") {
      setDetail(null);
      setDetailExpanded(false);
    }
    if (prevDetailRef.current?.kind === "report") setPrevDetail(null);
  }, []);
  const expandDetail = useCallback(() => setDetailExpanded(true), []);
  const collapseDetail = useCallback(() => setDetailExpanded(false), []);

  return {
    open,
    tab,
    detail,
    detailExpanded,
    openTab,
    close,
    setTab,
    openDetail,
    closeDetail,
    clearReportDetails,
    expandDetail,
    collapseDetail,
  };
}
